import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import {
  MdInfoOutline,
  MdOutlineImage,
  MdOutlineVideocam,
  MdOutlineViewInAr,
} from "react-icons/md";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import SearchInput from "../../../common/components/SearchInput/SearchInput";
import { formatContentTypeName } from "../util/NameFormatter";
import { setAlert } from "../../alert/state/alertsSlice";
import type {
  RelinkOperation,
  RelinkTrigger,
  RelinkUndoPayload,
  SceneNavigationOptions,
} from "../state/graphLegacy";
import {
  fetchSceneNavigationOptions,
  relinkScene,
  selectAllGraphContents,
  selectAllGraphContentTypes,
  selectAllGraphActions,
  getGraphLayout,
} from "../state/graphLegacy";
import { buildGraph } from "../util/graphBuilder";
import type { RelinkSimOp } from "../util/simulateRelink";
import { simulateRelink } from "../util/simulateRelink";
import RelinkPreviewGraph from "./RelinkPreviewGraph";

const RELINK_COMPONENT = "relinkScene";

// "Forward / success" moments shown by default; everything else (failure / edge
// outcomes like onFail, onUnfinished, onDeactivate) is tucked under "advanced".
const SIMPLE_EVENTS = [
  "onVideoEnd",
  "onFilled",
  "onEnd",
  "onAudioEnd",
  "onTutoEnd",
  "onGameEnd",
  "onFinished",
];

export interface RelinkSceneOption {
  contentSceneId: number;
  contentId: number;
  label: string;
  sceneType?: "image360" | "video360" | "model3d";
  isStartScene?: boolean;
  interactionCount?: number;
}

// Same icon-per-scene-type affordance as SceneTypeDropdown (stats heatmap).
const SceneTypeIcon: React.FC<{ sceneType?: RelinkSceneOption["sceneType"] }> = ({ sceneType }) => {
  const cls = "h-4 w-4 text-gray-500 flex-shrink-0";
  if (sceneType === "video360") return <MdOutlineVideocam className={cls} />;
  if (sceneType === "model3d") return <MdOutlineViewInAr className={cls} />;
  return <MdOutlineImage className={cls} />;
};

interface RelinkSceneModalProps {
  isOpen: boolean;
  onClose: () => void;
  // The orphan scene O being relinked
  targetContentId: number;
  targetContentSceneId: number;
  targetLabel?: string;
  // Candidate source scenes (all scenes currently in the graph)
  sceneOptions: RelinkSceneOption[];
  projectId: number;
  // Pre-selected source scene (e.g. "create next scene": the source is the
  // scene the user started from, only the moment remains to pick).
  initialSourceContentSceneId?: number | null;
  // Receives the backend's undo payload so the caller can offer an "undo".
  onSuccess?: (undo?: RelinkUndoPayload | null) => void;
}

// A normalized, presentation-friendly "moment" derived from the backend options.
interface Moment {
  key: string;
  kind: RelinkTrigger["triggerMode"]; // videoEnd | interaction | newButton
  triggerContentId?: number;
  eventTypeId?: number;
  eventName?: string;
  interactionName?: string;
  occupied: boolean;
  activateActionId?: number;
  currentTargetSceneContentId?: number;
  currentTargetSceneName?: string | null;
  advanced: boolean;
}

const buildMoments = (options: SceneNavigationOptions): Moment[] => {
  const moments: Moment[] = [];
  // Recommended, universal default — always valid, never in conflict.
  moments.push({ key: "newButton", kind: "newButton", occupied: false, advanced: false });

  if (options.sceneIsVideo && options.videoEnd) {
    const v = options.videoEnd;
    moments.push({
      key: "videoEnd",
      kind: "videoEnd",
      eventTypeId: v.eventTypeId,
      eventName: "onVideoEnd",
      occupied: v.occupied,
      activateActionId: v.activateActionId,
      currentTargetSceneContentId: v.currentTargetSceneContentId,
      currentTargetSceneName: v.currentTargetSceneName,
      advanced: false,
    });
  }

  for (const it of options.interactions) {
    // The interaction's own name, falling back to its type label when empty.
    const interactionName = it.name || formatContentTypeName(it.modelName);
    for (const e of it.events) {
      moments.push({
        key: `i-${it.contentId}-${e.eventTypeId}`,
        kind: "interaction",
        triggerContentId: it.contentId,
        eventTypeId: e.eventTypeId,
        eventName: e.name,
        interactionName,
        occupied: e.occupied,
        activateActionId: e.activateActionId,
        currentTargetSceneContentId: e.currentTargetSceneContentId,
        currentTargetSceneName: e.currentTargetSceneName,
        advanced: !SIMPLE_EVENTS.includes(e.name),
      });
    }
  }
  return moments;
};

const momentToTrigger = (m: Moment): RelinkTrigger => {
  if (m.kind === "newButton") return { triggerMode: "newButton" };
  if (m.kind === "videoEnd") return { triggerMode: "videoEnd", eventTypeId: m.eventTypeId };
  return {
    triggerMode: "interaction",
    triggerContentId: m.triggerContentId,
    eventTypeId: m.eventTypeId,
  };
};

const RelinkSceneModal: React.FC<RelinkSceneModalProps> = ({
  isOpen,
  onClose,
  targetContentId,
  targetContentSceneId,
  targetLabel,
  sceneOptions,
  projectId,
  initialSourceContentSceneId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const targetName = targetLabel || t("pages.project.edit.unlinkedScene");

  const [sourceSceneId, setSourceSceneId] = useState<number | null>(null);
  const [entryOptions, setEntryOptions] = useState<SceneNavigationOptions | null>(null);
  const [selectedKey, setSelectedKey] = useState<string>("newButton");
  // For an occupied entry moment: insert O in the middle, or redirect to O.
  const [occupiedOp, setOccupiedOp] = useState<"insert" | "redirect">("insert");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);

  // Insert flow: pick the outgoing moment on O (O -> S2).
  const [step, setStep] = useState<"main" | "exit">("main");
  const [exitOptions, setExitOptions] = useState<SceneNavigationOptions | null>(null);
  const [exitKey, setExitKey] = useState<string>("newButton");
  const [pendingEntry, setPendingEntry] = useState<
    (RelinkTrigger & { sourceContentSceneId: number }) | null
  >(null);
  const [pendingTargetName, setPendingTargetName] = useState<string>("");
  const [pendingS2ContentId, setPendingS2ContentId] = useState<number | null>(null);

  // Searchable source picker (combobox)
  const [sourceSearch, setSourceSearch] = useState("");
  const [sourceListOpen, setSourceListOpen] = useState(false);
  // Candidate hovered in the source list — drives a live preview before any click.
  const [hoverSourceSceneId, setHoverSourceSceneId] = useState<number | null>(null);

  const sources = useMemo(
    () => sceneOptions.filter((s) => s.contentSceneId !== targetContentSceneId),
    [sceneOptions, targetContentSceneId],
  );

  const filteredSources = useMemo(() => {
    const q = sourceSearch.trim().toLowerCase();
    if (!q) return sources;
    return sources.filter((s) => s.label.toLowerCase().includes(q));
  }, [sources, sourceSearch]);

  const sourceName = useMemo(
    () => sources.find((s) => s.contentSceneId === sourceSceneId)?.label ?? "",
    [sources, sourceSceneId],
  );

  useEffect(() => {
    if (!isOpen) {
      setSourceSceneId(null);
      setSourceSearch("");
      setSourceListOpen(false);
      setHoverSourceSceneId(null);
      setEntryOptions(null);
      setSelectedKey("newButton");
      setOccupiedOp("insert");
      setShowAdvanced(false);
      setLoading(false);
      setStep("main");
      setExitOptions(null);
      setExitKey("newButton");
      setPendingEntry(null);
      setPendingTargetName("");
      setPendingS2ContentId(null);
    }
  }, [isOpen]);

  const loadOptions = useCallback(
    async (sceneContentSceneId: number): Promise<SceneNavigationOptions | null> => {
      try {
        return await dispatch(
          fetchSceneNavigationOptions({
            sceneContentSceneId,
            componentId: "relinkNavOptions",
          }),
        ).unwrap();
      } catch {
        dispatch(
          setAlert({ isOpen: true, msg: t("pages.project.edit.relinkError"), type: "danger" }),
        );
        return null;
      }
    },
    [dispatch, t],
  );

  // Guards against out-of-order responses: selecting scene A then quickly
  // scene B must never show A's moments under B (submitting would then send
  // an activateActionId belonging to the wrong scene).
  const sourceReqIdRef = useRef(0);

  const handleSourceSelect = useCallback(
    async (id: number) => {
      const reqId = ++sourceReqIdRef.current;
      setSourceSceneId(id);
      setEntryOptions(null);
      setSelectedKey("newButton");
      setOccupiedOp("insert");
      setShowAdvanced(false);
      setLoading(true);
      const options = await loadOptions(id);
      if (reqId !== sourceReqIdRef.current) return; // a newer selection won
      setLoading(false);
      if (options) setEntryOptions(options);
    },
    [loadOptions],
  );

  // When the caller already knows the source ("create next scene"), select it
  // on open so the user lands directly on the moment picker. The user can
  // still change the source afterwards.
  useEffect(() => {
    if (isOpen && initialSourceContentSceneId != null) {
      // The combobox displays sourceSearch: mirror the selection there too,
      // exactly like a manual pick from the list does.
      const match = sources.find((s) => s.contentSceneId === initialSourceContentSceneId);
      if (match) setSourceSearch(match.label);
      void handleSourceSelect(initialSourceContentSceneId);
    }
  }, [isOpen, initialSourceContentSceneId]);

  const submit = useCallback(
    async (
      operation: RelinkOperation,
      entry: RelinkTrigger & { sourceContentSceneId: number },
      exit?: RelinkTrigger,
    ) => {
      try {
        const res = await dispatch(
          relinkScene({
            projectId,
            targetContentId,
            operation,
            entry,
            exit,
            componentId: RELINK_COMPONENT,
          }),
        ).unwrap();
        dispatch(
          setAlert({ isOpen: true, msg: t("pages.project.edit.relinkSuccess"), type: "success" }),
        );
        onSuccess?.((res.data?.undo as RelinkUndoPayload | undefined) ?? null);
        onClose();
      } catch {
        dispatch(
          setAlert({ isOpen: true, msg: t("pages.project.edit.relinkError"), type: "danger" }),
        );
      }
    },
    [dispatch, projectId, targetContentId, t, onSuccess, onClose],
  );

  const entryMoments = useMemo(
    () => (entryOptions ? buildMoments(entryOptions) : []),
    [entryOptions],
  );
  const exitMoments = useMemo(() => (exitOptions ? buildMoments(exitOptions) : []), [exitOptions]);
  const selectedMoment = entryMoments.find((m) => m.key === selectedKey) ?? null;
  const selectedExit = exitMoments.find((m) => m.key === exitKey) ?? null;

  // Event wording mirrors Unity's GetEventTypeReadableName (loc_link_*), prefixed
  // with the interaction's name so identical events stay distinguishable.
  const momentLabel = (m: Moment): string => {
    if (m.kind === "newButton") return t("pages.project.edit.relinkEvent.newButton");
    if (m.kind === "videoEnd") return t("pages.project.edit.relinkEvent.onVideoEnd");
    const eventLabel = t(`pages.project.edit.relinkEvent.${m.eventName}`);
    return `${m.interactionName} — ${eventLabel}`;
  };

  // A trigger that already leads to THIS scene: replace would be a no-op and
  // insert would create a self-loop. Offer nothing actionable for it.
  const leadsToSelf = useCallback(
    (m: Moment | null): boolean =>
      !!m && m.occupied && m.currentTargetSceneContentId === targetContentId,
    [targetContentId],
  );

  // --- live graph data, for the change preview ------------------------------
  const liveContents = useAppSelector(selectAllGraphContents);
  const liveContentTypes = useAppSelector(selectAllGraphContentTypes);
  const liveActions = useAppSelector(selectAllGraphActions);
  // The live graph is already memoized app-wide; reuse it for the "before" diff
  // instead of rebuilding it here.
  const liveGraph = useAppSelector(getGraphLayout);

  const sourceContentId = useMemo(
    () => sources.find((s) => s.contentSceneId === sourceSceneId)?.contentId,
    [sources, sourceSceneId],
  );

  // Topology of the scene being relinked (O), to support "move + bridge" when O
  // is ALREADY linked: detach its current incoming link, reconnect that upstream
  // to O's successor, then re-attach O at the new spot. Auto only when simple
  // (exactly 1 incoming activation + 1 child scene); otherwise we warn.
  const oTopology = useMemo(() => {
    const visibleNodes = (liveGraph?.[0] ?? []) as any[];
    const oNode = visibleNodes.find(
      (n) => n.type === "scene" && Number(n.contentId) === Number(targetContentId),
    );
    const incoming = liveActions.filter(
      (a) => Number(a.affected_content) === Number(targetContentId) && a.is_activate,
    );
    const childScenes: any[] = oNode ? Array.from(oNode.children as Set<any>) : [];
    const isLinked = incoming.length > 0;
    // Bridgeable as long as O has exactly ONE successor — any number of incoming
    // links can all be re-pointed to it.
    const canBridge = childScenes.length === 1;
    const succContentId = canBridge ? Number(childScenes[0].contentId) : null;
    const oAssetIds = new Set<number>(
      oNode ? Array.from(oNode.assets as Set<any>).map((a: any) => Number(a.contentId)) : [],
    );
    // ALL of O's actions toward its successor: several triggers can lead to
    // the same scene (e.g. onVideoEnd + a button) and every one must go when
    // O's outgoing edge is dropped, or a stale edge survives the move.
    let removeOutgoingIds: number[] = [];
    if (succContentId != null) {
      removeOutgoingIds = liveActions
        .filter(
          (a) =>
            Number(a.affected_content) === succContentId &&
            a.is_activate &&
            (Number(a.affect_content) === Number(targetContentId) ||
              oAssetIds.has(Number(a.affect_content))),
        )
        .map((a) => Number(a.id));
    }
    return {
      isLinked,
      canBridge,
      incomingActivateIds: incoming.map((a) => Number(a.id)),
      succContentId,
      removeOutgoingIds,
    };
  }, [liveGraph, liveActions, targetContentId]);

  // The "move" payload for a linked O with a single successor: bridge ALL of its
  // incoming links to that successor, plus an optional loop-guard removal (when
  // the new source is O's own successor, drop O's old outgoing edge).
  const buildMovePayload = useCallback(
    (
      newSourceContentId: number | undefined,
      // insert replaces O's outgoing with a fresh exit edge, so always drop the old
      // one. add/redirect keep O's outgoing, except the loop case (new source = succ).
      alwaysRemoveOutgoing = false,
    ) => {
      if (
        !oTopology.isLinked ||
        !oTopology.canBridge ||
        oTopology.incomingActivateIds.length === 0
      ) {
        return undefined;
      }
      const loop = newSourceContentId != null && newSourceContentId === oTopology.succContentId;
      const dropOutgoing = (alwaysRemoveOutgoing || loop) && oTopology.removeOutgoingIds.length > 0;
      return {
        bridgeActivateActionIds: oTopology.incomingActivateIds,
        bridgeToContentId: oTopology.succContentId,
        removeActivateActionIds: dropOutgoing ? oTopology.removeOutgoingIds : undefined,
      };
    },
    [oTopology],
  );

  // Bridge redirects + removals contributed by a move payload (shared helper).
  const moveOps = useCallback((move: ReturnType<typeof buildMovePayload>) => {
    const redirects =
      move && move.bridgeToContentId != null
        ? move.bridgeActivateActionIds.map((id) => ({
            activateActionId: id,
            toContentId: move.bridgeToContentId as number,
          }))
        : [];
    const removals = (move?.removeActivateActionIds ?? []).map((id) => ({
      activateActionId: id,
    }));
    return { redirects, removals };
  }, []);

  // Describe the pending operation as a simulation op (redirects + new edges).
  // A "button" edge materialises a real button content so buildGraph lays it out
  // as a sibling interaction — adding a path WITHOUT removing existing links.
  const previewOp = useMemo<RelinkSimOp | null>(() => {
    if (sourceContentId == null) return null;
    const O = targetContentId;

    if (step === "exit") {
      if (!pendingEntry?.activateActionId || !selectedExit || pendingS2ContentId == null) {
        return null;
      }
      const exitEdge: RelinkSimOp["edges"][number] =
        selectedExit.kind === "newButton"
          ? { kind: "button", hostSceneContentId: O, targetContentId: pendingS2ContentId }
          : {
              kind: "direct",
              affectContentId:
                selectedExit.kind === "interaction" ? Number(selectedExit.triggerContentId) : O,
              targetContentId: pendingS2ContentId,
              deactivateTargetContentId: O,
              eventTypeId: selectedExit.eventTypeId ?? 25,
            };
      // insert moves O: bridge its old incoming + drop its old outgoing (replaced
      // by the exit edge above).
      const move = buildMovePayload(sourceContentId, true);
      const { redirects: bridges, removals } = moveOps(move);
      return {
        redirects: [
          { activateActionId: pendingEntry.activateActionId, toContentId: O },
          ...bridges,
        ],
        edges: [exitEdge],
        removals,
      };
    }

    if (!selectedMoment || leadsToSelf(selectedMoment)) return null;
    const entryAffect =
      selectedMoment.kind === "interaction"
        ? Number(selectedMoment.triggerContentId)
        : sourceContentId;

    if (!selectedMoment.occupied) {
      const edge: RelinkSimOp["edges"][number] =
        selectedMoment.kind === "newButton"
          ? { kind: "button", hostSceneContentId: sourceContentId, targetContentId: O }
          : {
              kind: "direct",
              affectContentId: entryAffect,
              targetContentId: O,
              deactivateTargetContentId: sourceContentId,
              eventTypeId: selectedMoment.eventTypeId ?? 25,
            };
      // Move + bridge when O is already linked: re-point all of O's incoming
      // links to its single successor (O keeps its own outgoing here).
      const { redirects, removals } = moveOps(buildMovePayload(sourceContentId));
      return { redirects, edges: [edge], removals };
    }
    if (occupiedOp === "redirect") {
      // redirect also moves O when it is already placed (keeps O's outgoing).
      const { redirects: bridges, removals } = moveOps(buildMovePayload(sourceContentId));
      return {
        redirects: [
          { activateActionId: Number(selectedMoment.activateActionId), toContentId: O },
          ...bridges,
        ],
        edges: [],
        removals,
      };
    }
    // insert (main step): redirect entry to O + approximate the outgoing O -> S2.
    // Moving O here drops its old outgoing (replaced by the O -> S2 edge).
    const s2 = Number(selectedMoment.currentTargetSceneContentId);
    const { redirects: bridges, removals } = moveOps(buildMovePayload(sourceContentId, true));
    return {
      redirects: [
        { activateActionId: Number(selectedMoment.activateActionId), toContentId: O },
        ...bridges,
      ],
      edges: [
        {
          kind: "direct",
          affectContentId: O,
          targetContentId: s2,
          deactivateTargetContentId: O,
          eventTypeId: 25,
        },
      ],
      removals,
    };
  }, [
    sourceContentId,
    step,
    selectedMoment,
    selectedExit,
    occupiedOp,
    pendingEntry,
    pendingS2ContentId,
    targetContentId,
    buildMovePayload,
    moveOps,
    leadsToSelf,
  ]);

  // Hovering a candidate in the source list simply LOCATES that scene in the
  // current graph (highlighted, everything else dimmed) — no choice has been
  // made yet, so nothing is simulated.
  const hoverContentId = useMemo(
    () => sources.find((s) => s.contentSceneId === hoverSourceSceneId)?.contentId,
    [sources, hoverSourceSceneId],
  );
  const hoverPreview = useMemo(() => {
    if (!sourceListOpen || hoverContentId == null) return null;
    const [liveNodes, liveLayout, liveLinks] = liveGraph;
    return {
      nodes: liveNodes,
      layout: liveLayout,
      links: liveLinks,
      greenEdges: [],
      newButtonContentIds: [],
      involvedContentIds: [hoverContentId],
      detachedNames: [],
      highlightContentIds: [hoverContentId],
    };
  }, [sourceListOpen, hoverContentId, liveGraph]);

  const preview = useMemo(() => {
    if (!previewOp) return null;
    try {
      const [, , , liveHidden] = liveGraph;
      const sim = simulateRelink(liveContents, liveContentTypes, liveActions, previewOp, projectId);
      // Force showAllAssets so the new button (an asset) is shown even when its
      // host scene branches to several scenes.
      const [simNodes, simLayout, simLinks, simHidden] = buildGraph(
        sim.contents,
        sim.contentTypes,
        sim.actions,
        { showAllAssets: true },
      );
      const liveHiddenIds = new Set(liveHidden.map((h: any) => Number(h.contentTypeId)));
      const detachedNames = simHidden
        .filter((h: any) => !liveHiddenIds.has(Number(h.contentTypeId)))
        .map((h: any) => h.details?.name)
        .filter((n: any): n is string => Boolean(n));
      const involved = new Set<number>();
      sim.greenEdges.forEach((e) => {
        involved.add(e.fromContentId);
        involved.add(e.toContentId);
      });
      return {
        nodes: simNodes,
        layout: simLayout,
        links: simLinks,
        greenEdges: sim.greenEdges,
        newButtonContentIds: sim.newButtonContentIds,
        involvedContentIds: Array.from(involved),
        detachedNames,
        highlightContentIds: [] as number[],
      };
    } catch {
      return null;
    }
  }, [previewOp, liveGraph, liveContents, liveContentTypes, liveActions, projectId]);

  // The transient hover locator wins over the committed selection's preview.
  const shownPreview = hoverPreview ?? preview;

  // --- main-step action -----------------------------------------------------
  const handlePrimary = async () => {
    if (sourceSceneId == null || !selectedMoment) return;
    const entry = { ...momentToTrigger(selectedMoment), sourceContentSceneId: sourceSceneId };

    if (!selectedMoment.occupied) {
      const move = buildMovePayload(sourceContentId);
      void submit("add", move ? { ...entry, move } : entry);
      return;
    }
    if (occupiedOp === "redirect") {
      const move = buildMovePayload(sourceContentId);
      void submit("replace", {
        ...entry,
        activateActionId: selectedMoment.activateActionId,
        ...(move ? { move } : {}),
      });
      return;
    }
    // insert -> need the outgoing moment on O. O is moved here (drop old outgoing).
    const move = buildMovePayload(sourceContentId, true);
    setPendingEntry({
      ...entry,
      activateActionId: selectedMoment.activateActionId,
      ...(move ? { move } : {}),
    });
    setPendingTargetName(selectedMoment.currentTargetSceneName ?? "");
    setPendingS2ContentId(selectedMoment.currentTargetSceneContentId ?? null);
    setExitKey("newButton");
    setLoading(true);
    const options = await loadOptions(targetContentSceneId);
    setLoading(false);
    if (options) {
      setExitOptions(options);
      setStep("exit");
    }
  };

  const handleInsertConfirm = () => {
    if (!pendingEntry || !selectedExit) return;
    void submit("insert", pendingEntry, momentToTrigger(selectedExit));
  };

  // An exit moment occupied by the very action the pending move deletes is
  // actually free: the backend removes it BEFORE the occupancy check.
  const exitFreedByMove = (m: Moment): boolean =>
    m.occupied &&
    (pendingEntry?.move?.removeActivateActionIds ?? []).some(
      (id) => Number(m.activateActionId) === Number(id),
    );

  // An occupied exit trigger can't host a fresh outgoing edge — unless freed.
  const exitDisabled = (m: Moment): boolean => m.occupied && !exitFreedByMove(m);

  if (!isOpen) return null;

  // --- shared sub-renderers --------------------------------------------------

  const renderMomentRow = (
    m: Moment,
    checked: boolean,
    onSelect: () => void,
    radioName: string,
    disabled = false,
  ) => (
    <label
      key={m.key}
      className={`flex items-start gap-3 px-3 py-2 border-b border-gray-100 last:border-b-0 text-sm ${
        disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50 cursor-pointer"
      } ${checked ? "bg-green-50" : ""}`}
    >
      <input
        type="radio"
        name={radioName}
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
        className="mt-0.5"
      />
      <span className="flex-1">
        <span className="text-gray-900">{momentLabel(m)}</span>
        {m.kind === "newButton" && (
          <>
            <span className="ml-2 badge-sucess text-[10px] align-middle">
              {t("pages.project.edit.relinkRecommended")}
            </span>
            <span className="block text-xs text-gray-500 mt-0.5">
              <MdInfoOutline className="inline-block mr-1 text-gray-400" />
              {t("pages.project.edit.relinkNewButtonHelp")}
            </span>
          </>
        )}
        {m.occupied && m.kind !== "newButton" && (
          <span className="block text-xs text-amber-600 mt-0.5">
            {leadsToSelf(m)
              ? t("pages.project.edit.relinkAlreadyHere")
              : t("pages.project.edit.relinkOccupiedQuestion", {
                  scene: m.currentTargetSceneName ?? "",
                })}
          </span>
        )}
      </span>
    </label>
  );

  // Insert/redirect sub-choice for an occupied entry moment, with mini-diagrams.
  const renderOccupiedSubChoice = (m: Moment) => {
    const s2 = m.currentTargetSceneName ?? "";
    return (
      <div className="mx-3 mb-3 mt-1 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
        <label className="flex items-start gap-3 cursor-pointer mb-2">
          <input
            type="radio"
            name="occupiedOp"
            checked={occupiedOp === "insert"}
            onChange={() => setOccupiedOp("insert")}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium text-gray-900">
              {t("pages.project.edit.relinkInsertOption")}
            </span>
            <span className="block text-xs text-gray-600 mt-0.5 font-mono">
              {sourceName} → {targetName} → {s2}
            </span>
            <span className="block text-xs text-gray-500 mt-0.5">
              {t("pages.project.edit.relinkInsertNextHint", { name: targetName, scene: s2 })}
            </span>
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name="occupiedOp"
            checked={occupiedOp === "redirect"}
            onChange={() => setOccupiedOp("redirect")}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium text-gray-900">
              {t("pages.project.edit.relinkRedirectOption")}
            </span>
            <span className="block text-xs text-gray-600 mt-0.5 font-mono">
              {sourceName} → {targetName}
            </span>
            <span className="block text-xs text-amber-700 mt-0.5">
              ⚠ {t("pages.project.edit.relinkRedirectWarning", { scene: s2 })}
            </span>
          </span>
        </label>
      </div>
    );
  };

  const simpleEntry = entryMoments.filter((m) => !m.advanced);
  const advancedEntry = entryMoments.filter((m) => m.advanced);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[680px] max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("pages.project.edit.relinkModalTitle", { name: targetName })}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content — two columns: choices (left, scroll) | live preview (right).
            Fixed-size, so the modal never resizes and the choices keep their full
            height regardless of the preview. */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left: the choices */}
          <div className="flex-1 flex flex-col overflow-hidden p-4 border-r border-gray-100">
            {step === "main" ? (
              <>
                <div className="flex items-start gap-2 mb-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-3 flex-shrink-0">
                  <MdInfoOutline className="text-gray-400 mt-0.5 flex-shrink-0" />
                  <span>{t("pages.project.edit.relinkIntro")}</span>
                </div>

                {/* Step 1: source scene (searchable) */}
                <label className="block text-sm font-medium text-gray-700 mb-1 flex-shrink-0">
                  {t("pages.project.edit.relinkSourceLabel")}
                </label>
                <div className="relative mb-3 flex-shrink-0">
                  <SearchInput
                    value={sourceSearch}
                    onChange={(v) => {
                      setSourceSearch(v);
                      setSourceListOpen(true);
                    }}
                    onFocus={() => setSourceListOpen(true)}
                    onBlur={() => setTimeout(() => setSourceListOpen(false), 150)}
                    placeholder={t("pages.project.edit.relinkSourcePlaceholder")}
                  />
                  {sourceListOpen && (
                    <div
                      className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg"
                      onMouseLeave={() => setHoverSourceSceneId(null)}
                    >
                      {filteredSources.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-400">
                          {t("pages.project.edit.relinkNoSceneFound")}
                        </div>
                      ) : (
                        filteredSources.map((s) => (
                          <button
                            key={s.contentSceneId}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() => setHoverSourceSceneId(s.contentSceneId)}
                            onClick={() => {
                              setSourceSearch(s.label);
                              setSourceListOpen(false);
                              setHoverSourceSceneId(null);
                              void handleSourceSelect(s.contentSceneId);
                            }}
                            className={`flex w-full items-center gap-2 text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                              sourceSceneId === s.contentSceneId
                                ? "bg-green-50 text-green-700 font-medium"
                                : "text-gray-900"
                            }`}
                          >
                            <SceneTypeIcon sceneType={s.sceneType} />
                            <span className="flex-1 truncate">{s.label}</span>
                            {s.isStartScene && (
                              <span className="badge-sucess text-[10px] flex-shrink-0">
                                {t("pages.project.edit.relinkStartSceneBadge")}
                              </span>
                            )}
                            {s.interactionCount != null && (
                              <span className="text-xs text-gray-400 flex-shrink-0">
                                {t("pages.project.edit.relinkInteractionCount", {
                                  count: s.interactionCount,
                                })}
                              </span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {entryOptions && !loading && (
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex-shrink-0">
                    {t("pages.project.edit.relinkMomentLabel", { name: targetName })}
                  </label>
                )}

                {/* Step 2: moment list (scrolls internally) */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {loading && <div className="text-sm text-gray-500">{t("general.loading")}</div>}
                  {entryOptions && !loading && (
                    <>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        {simpleEntry.map((m) =>
                          renderMomentRow(
                            m,
                            selectedKey === m.key,
                            () => {
                              setSelectedKey(m.key);
                              setOccupiedOp("insert");
                            },
                            "entryMoment",
                          ),
                        )}
                      </div>

                      {advancedEntry.length > 0 && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => setShowAdvanced((v) => !v)}
                            className="text-sm text-green-600 hover:text-green-700 font-medium"
                          >
                            <span className={`inline-block ${showAdvanced ? "" : "-rotate-90"}`}>
                              ▾
                            </span>{" "}
                            {t("pages.project.edit.relinkMoreMoments")}
                          </button>
                          {showAdvanced && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden mt-1">
                              {advancedEntry.map((m) =>
                                renderMomentRow(
                                  m,
                                  selectedKey === m.key,
                                  () => {
                                    setSelectedKey(m.key);
                                    setOccupiedOp("insert");
                                  },
                                  "entryMoment",
                                ),
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Occupied -> insert / redirect */}
                      {selectedMoment?.occupied &&
                        !leadsToSelf(selectedMoment) &&
                        renderOccupiedSubChoice(selectedMoment)}

                      {/* Already-linked scene that's too branched to auto-bridge
                          (applies to add, redirect and insert alike) */}
                      {selectedMoment &&
                        !leadsToSelf(selectedMoment) &&
                        oTopology.isLinked &&
                        !oTopology.canBridge && (
                          <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
                            ⚠ {t("pages.project.edit.relinkMoveWarn")}
                          </div>
                        )}
                    </>
                  )}
                </div>
              </>
            ) : (
              /* Insert: choose the outgoing moment on O */
              <>
                <p className="text-sm font-medium text-gray-700 mb-2 flex-shrink-0">
                  {t("pages.project.edit.relinkExitLabel", {
                    name: targetName,
                    scene: pendingTargetName,
                  })}
                </p>
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {loading && <div className="text-sm text-gray-500">{t("general.loading")}</div>}
                  {exitOptions && !loading && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      {exitMoments
                        .filter((m) => !m.advanced)
                        .map((m) =>
                          renderMomentRow(
                            m,
                            exitKey === m.key,
                            () => setExitKey(m.key),
                            "exitMoment",
                            exitDisabled(m),
                          ),
                        )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right: live preview (always present) */}
          <div className="w-[480px] flex-shrink-0 p-4 flex flex-col bg-gray-50/40">
            <p className="text-sm font-medium text-gray-700 mb-1 flex-shrink-0">
              {t("pages.project.edit.relinkPreview")}
            </p>
            <div className="flex-1 min-h-0">
              {shownPreview ? (
                <RelinkPreviewGraph
                  nodes={shownPreview.nodes}
                  layout={shownPreview.layout}
                  links={shownPreview.links}
                  greenEdges={shownPreview.greenEdges}
                  newButtonContentIds={shownPreview.newButtonContentIds}
                  involvedContentIds={shownPreview.involvedContentIds}
                  detachedNames={shownPreview.detachedNames}
                  highlightContentIds={shownPreview.highlightContentIds}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-center text-sm text-gray-400 bg-[#fafafa] border border-[#eee] rounded-xl p-4">
                  {t("pages.project.edit.relinkPreviewPlaceholder")}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
          {step === "exit" ? (
            <button
              onClick={() => {
                setStep("main");
                setExitOptions(null);
                setPendingEntry(null);
              }}
              className="text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              &larr; {t("general.back")}
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="btn-alternative-outline">
              {t("general.cancel")}
            </button>
            <IsLoading
              componentId={RELINK_COMPONENT}
              showSuccess={false}
              spinnerStyle={{ width: 20, height: 20, position: "static", margin: 0 }}
            >
              {step === "main" ? (
                <button
                  onClick={handlePrimary}
                  disabled={
                    !entryOptions || loading || !selectedMoment || leadsToSelf(selectedMoment)
                  }
                  className="btn-primary-fill"
                >
                  {selectedMoment?.occupied &&
                  occupiedOp === "insert" &&
                  !leadsToSelf(selectedMoment)
                    ? t("pages.project.edit.relinkNext")
                    : t("pages.project.edit.relinkSubmit")}
                </button>
              ) : (
                <button
                  onClick={handleInsertConfirm}
                  disabled={!selectedExit || exitDisabled(selectedExit) || loading}
                  className="btn-primary-fill"
                >
                  {t("pages.project.edit.relinkSubmit")}
                </button>
              )}
            </IsLoading>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelinkSceneModal;
